import { useEffect } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cx } from './cx'

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl'

const SIZE: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
}

/**
 * The one dialog shell. z-50 is the modal layer in the stacking contract
 * (dropdown 10 / sticky 20 / overlay 40 / modal 50) — do not invent new levels.
 *
 * `scrollable` switches the panel to a flex column capped at 80vh, which is what
 * the log and picker dialogs need; the body then scrolls, not the page.
 */
export default function Modal({
  open = true,
  onClose,
  title,
  size = 'md',
  scrollable,
  footer,
  className,
  children,
}: {
  open?: boolean
  onClose: () => void
  title?: ReactNode
  size?: ModalSize
  scrollable?: boolean
  footer?: ReactNode
  className?: string
  children: ReactNode
}) {
  const { t } = useTranslation()

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        className={cx(
          'bg-white rounded-xl shadow-xl w-full',
          SIZE[size],
          scrollable && 'flex flex-col max-h-[80vh]',
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="flex items-start justify-between gap-3 px-6 pt-5 pb-3 border-b border-gray-100 shrink-0">
            <h2 className="text-base font-semibold text-gray-900 min-w-0">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('common.close')}
              className="text-gray-400 hover:text-gray-600 shrink-0 -mt-0.5 text-lg leading-none"
            >
              ×
            </button>
          </div>
        )}
        <div className={cx('px-6 py-5', scrollable && 'overflow-y-auto min-h-0 flex-1')}>{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}
